import { once } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { ALLOCATIONS, EXPERIMENTS } from '@fiverr-private/listing_experiments';
import { useIntersectionObserver } from '@fiverr-private/hooks';
import { allocateToClientExperiment } from '../../../../../clientExperiments/clientUtils';
import { useAppContext } from '../../../../context/listingsContext';
import { TestType } from '../HourlyRate.constants';

const ExperimentId = {
    User: EXPERIMENTS.DOLPHINS_HOURLY_RATE_BANNER_LISTINGS_USER,
    Guest: EXPERIMENTS.DOLPHINS_HOURLY_RATE_BANNER_LISTINGS_GUEST,
};

const isInTest = (experimentId: number, groupResult?: number) => {
    const { abTests = {} } = getContext();
    const group: string | number | undefined = abTests[experimentId] || groupResult;
    const inGroupB = group?.toString() === ALLOCATIONS.TEST_B;
    return { inGroupB };
};

const tryAllocateBanner = once(
    ({
        clientExperimentsGroups,
        activeExperiments,
    }: {
        clientExperimentsGroups: Record<number, string | number>;
        activeExperiments: Record<number, unknown>;
    }): number | undefined => {
        const { userId } = getContext();
        const experimentId = userId ? ExperimentId.User : ExperimentId.Guest;

        const inExperimentGroup = Number(activeExperiments[experimentId]);
        const inExperiment = inExperimentGroup > 0;

        if (inExperiment) {
            return inExperimentGroup;
        }

        if (isInTest(experimentId).inGroupB) {
            return;
        }

        const experimentGroup = Number(clientExperimentsGroups[experimentId]);
        if (experimentGroup > 0) {
            return allocateToClientExperiment(experimentId, clientExperimentsGroups);
        }
    }
);

export const useHourlyRateBannersAllocation = (observableRef: React.RefObject<Element>) => {
    const { clientExperimentsGroups, activeExperiments } = useAppContext();
    const isLoggedIn = !!getContext().userId;
    const [group, setGroup] = useState(Number(ALLOCATIONS.CONTROL));
    const entry = useIntersectionObserver(observableRef, { threshold: 0.5 });

    const runAllocation = useCallback(() => {
        const allocationGroup = tryAllocateBanner({ clientExperimentsGroups, activeExperiments });
        if (allocationGroup) {
            setGroup(allocationGroup);
        }
    }, [clientExperimentsGroups, activeExperiments]);

    useEffect(() => {
        if (!entry?.isIntersecting) {
            return;
        }

        runAllocation();
    }, [entry, entry?.isIntersecting, runAllocation]);

    return {
        testType: isLoggedIn ? TestType.USER : TestType.GUEST,
        guest: isInTest(ExperimentId.Guest, group),
        user: isInTest(ExperimentId.User, group),
    };
};
